/**
 * Client-side project persistence — used when `/api/pm/projects` is not live.
 * Mirrors the task-order-store pattern: localStorage is authoritative until
 * the backend ships project routes.
 *
 * Pure `apply*` helpers also power API PUT fallbacks when member sub-routes
 * are missing but `PUT /api/pm/projects/:id` can persist `members` JSON.
 */

import {
  projectId,
  normalizeMembers,
  PROJECT_ROLES,
  applyResponsibilityToProject,
  appendResponsibilityToProject,
  applyMemberUpdateToResponsibilities,
  getProjectResponsibilities,
  getNextResponsibilityOrder,
  syncMembersFromResponsibilities,
  reconcileProjectMembers,
  isValidResponsibilityNote,
} from './project-config'

const STORAGE_KEY = 'taskModuleProjects.v1'

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeStore(projects) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects))
  } catch (e) {
    console.warn('[project-store] could not persist projects:', e)
  }
}

function nextId(projects) {
  const nums = projects
    .map(p => projectId(p))
    .filter(id => id != null && String(id).startsWith('local-'))
    .map(id => Number(String(id).replace('local-', '')))
    .filter(n => !Number.isNaN(n))
  const max = nums.length ? Math.max(...nums) : 0
  return `local-${max + 1}`
}

export function getLocalProjects() {
  return readStore()
}

export function getLocalProject(projectIdValue) {
  if (projectIdValue == null) return null
  return readStore().find(p => String(projectId(p)) === String(projectIdValue)) || null
}

export function saveLocalProject(project) {
  const projects = readStore()
  const id = projectId(project)
  if (id != null) {
    const idx = projects.findIndex(p => String(projectId(p)) === String(id))
    if (idx >= 0) {
      projects[idx] = {
        ...projects[idx],
        ...project,
        members: normalizeMembers(project.members || projects[idx].members),
        responsibilities: Array.isArray(project.responsibilities)
          ? project.responsibilities
          : projects[idx].responsibilities,
      }
    } else {
      projects.push(project)
    }
  } else {
    const newProject = { ...project, id: nextId(projects) }
    projects.push(newProject)
    writeStore(projects)
    return newProject
  }
  writeStore(projects)
  return projects.find(p => String(projectId(p)) === String(id)) || project
}

export function deleteLocalProject(id) {
  const projects = readStore().filter(p => String(projectId(p)) !== String(id))
  writeStore(projects)
  return { status: 'OK' }
}

/** Pure: add/update a member (+ optional responsibility) on a project object. */
export function applyAddProjectMember(project, member, addedBy) {
  if (!project) return null
  let members = normalizeMembers(project.members)
  let responsibilities = getProjectResponsibilities(project)
  const empId = String(member.empId ?? member.emp_id)
  if (!empId) return null
  const existing = members.find(m => String(m.empId) === empId)

  if (existing) {
    members = members.map(m => (String(m.empId) === empId
      ? {
        ...m,
        role: member.role || m.role,
        name: member.name || m.name,
        email: member.email != null ? member.email : m.email,
      }
      : m))
  } else {
    members.push({
      empId,
      name: member.name || empId,
      email: member.email || null,
      role: member.role || PROJECT_ROLES.CONTRIBUTOR,
      responsibilityTitle: '',
      responsibilityDescription: '',
      responsibilityGroupId: null,
      sharedFromEmpId: null,
      attachments: [],
      addedAt: new Date().toISOString(),
      addedBy,
    })
  }

  const title = (member.responsibilityTitle || '').trim()
  const description = (member.responsibilityDescription || '').trim()
  const hasResponsibility = isValidResponsibilityNote(title) || isValidResponsibilityNote(description)

  if (hasResponsibility) {
    if (member.responsibilityGroupId) {
      const groupId = String(member.responsibilityGroupId)
      let found = false
      responsibilities = responsibilities.map(resp => {
        if (String(resp.id) !== groupId) return resp
        found = true
        if (resp.assignees.some(a => String(a.empId) === empId)) return resp
        return {
          ...resp,
          assignees: [
            ...resp.assignees,
            {
              empId,
              completed: false,
              completedAt: null,
              sharedFromEmpId: member.sharedFromEmpId || null,
            },
          ],
        }
      })
      if (!found) {
        responsibilities = [
          ...responsibilities,
          {
            id: groupId,
            responsibilityTitle: title,
            responsibilityDescription: description,
            responsibilityOrder: getNextResponsibilityOrder(responsibilities),
            attachments: member.attachments || [],
            assignees: [{
              empId,
              completed: false,
              completedAt: null,
              sharedFromEmpId: member.sharedFromEmpId || null,
            }],
          },
        ]
      }
    } else {
      const appended = appendResponsibilityToProject(
        { ...project, members, responsibilities },
        [empId],
        {
          responsibilityTitle: title,
          responsibilityDescription: description,
          attachments: member.attachments || [],
        },
      )
      members = appended.members
      responsibilities = appended.responsibilities
    }
  }

  members = syncMembersFromResponsibilities(members, responsibilities)
  return {
    ...project,
    members,
    responsibilities,
    updatedAt: new Date().toISOString(),
  }
}

/** Pure: patch an existing member / responsibility fields. */
export function applyUpdateProjectMember(project, member) {
  if (!project) return null
  const empId = String(member.empId ?? member.emp_id)
  if (!normalizeMembers(project.members).some(m => String(m.empId) === empId)) return null
  const { members, responsibilities } = applyMemberUpdateToResponsibilities(project, member)
  return {
    ...project,
    members,
    responsibilities,
    updatedAt: new Date().toISOString(),
  }
}

/** Pure: remove a member and drop them from responsibilities. */
export function applyRemoveProjectMember(project, empId) {
  if (!project) return null
  const id = String(empId)
  const members = normalizeMembers(project.members)
    .filter(m => String(m.empId) !== id)
  const responsibilities = getProjectResponsibilities(project)
    .map(resp => ({
      ...resp,
      assignees: (resp.assignees || []).filter(a => String(a.empId) !== id),
    }))
    .filter(resp => resp.assignees.length > 0)
  return {
    ...project,
    members: syncMembersFromResponsibilities(members, responsibilities),
    responsibilities,
    updatedAt: new Date().toISOString(),
  }
}

/** Pure: share an existing responsibility with more assignees. */
export function applyAssignResponsibility(project, sourceEmpId, targetEmpIds, responsibilityOverride = null) {
  if (!project) return null
  const { members, responsibilities } = applyResponsibilityToProject(
    project,
    sourceEmpId,
    targetEmpIds,
    responsibilityOverride,
  )
  return {
    ...project,
    members,
    responsibilities,
    updatedAt: new Date().toISOString(),
  }
}

/** Pure: append a new responsibility step for existing members. */
export function applyAddResponsibility(project, targetEmpIds, responsibility) {
  if (!project) return null
  const { members, responsibilities } = appendResponsibilityToProject(
    project,
    targetEmpIds,
    responsibility,
  )
  return reconcileProjectMembers({
    ...project,
    members,
    responsibilities,
    updatedAt: new Date().toISOString(),
  })
}

function persistApplied(projectIdValue, updated) {
  if (!updated) return null
  const projects = readStore()
  const idx = projects.findIndex(p => String(projectId(p)) === String(projectIdValue))
  if (idx < 0) {
    // Project only existed in memory (API-loaded) — mirror into local store.
    projects.push({ ...updated, id: updated.id ?? projectIdValue })
  } else {
    projects[idx] = updated
  }
  writeStore(projects)
  return updated
}

export function addLocalProjectMember(projectIdValue, member, addedBy) {
  const project = getLocalProject(projectIdValue)
  return persistApplied(projectIdValue, applyAddProjectMember(project, member, addedBy))
}

export function updateLocalProjectMember(projectIdValue, member) {
  const project = getLocalProject(projectIdValue)
  return persistApplied(projectIdValue, applyUpdateProjectMember(project, member))
}

export function removeLocalProjectMember(projectIdValue, empId) {
  const project = getLocalProject(projectIdValue)
  return persistApplied(projectIdValue, applyRemoveProjectMember(project, empId))
}

export function assignLocalResponsibilityToMembers(projectIdValue, sourceEmpId, targetEmpIds, responsibilityOverride = null) {
  const project = getLocalProject(projectIdValue)
  return persistApplied(
    projectIdValue,
    applyAssignResponsibility(project, sourceEmpId, targetEmpIds, responsibilityOverride),
  )
}

export function addLocalResponsibility(projectIdValue, targetEmpIds, responsibility) {
  const project = getLocalProject(projectIdValue)
  return persistApplied(
    projectIdValue,
    applyAddResponsibility(project, targetEmpIds, responsibility),
  )
}
